// Keep the browser's one-shot event across Turbo page visits, never in storage.
export function createInstallManager(browser) {
  let deferredPrompt = null
  let state = "manual"
  const subscribers = new Set()
  const displayMode = browser.matchMedia("(display-mode: standalone)")
  const snapshot = () => ({
    state: displayMode.matches || browser.navigator.standalone ? "standalone" : state,
    available: deferredPrompt !== null
  })
  const notify = () => subscribers.forEach(callback => callback(snapshot()))

  browser.addEventListener("beforeinstallprompt", event => {
    event.preventDefault()
    deferredPrompt = event
    state = "ready"
    notify()
  })
  browser.addEventListener("appinstalled", () => {
    deferredPrompt = null
    state = "installed"
    notify()
  })
  displayMode.addEventListener("change", notify)

  return {
    subscribe(callback) {
      subscribers.add(callback)
      callback(snapshot())
      return () => subscribers.delete(callback)
    },
    async install() {
      if (!deferredPrompt || snapshot().state === "standalone") return
      const event = deferredPrompt
      deferredPrompt = null
      state = "pending"
      notify()
      try {
        // Must run directly in the click handler, before any await.
        const result = await event.prompt()
        const choice = result || await event.userChoice
        if (state === "pending" && choice?.outcome === "dismissed") state = "dismissed"
      } catch (_error) {
        if (state === "pending") state = "error"
      }
      notify()
    }
  }
}

export function installationPlatform(navigator) {
  const ua = navigator.userAgent || ""
  if (/FBAN|FBAV|Instagram|Line\/|; wv\)/i.test(ua)) return "other"
  if (/iPad|iPhone|iPod/.test(ua) || (/Mac/.test(navigator.platform || "") && navigator.maxTouchPoints > 1)) return "ios"
  if (/Android/.test(ua)) return "android"
  if (/Chrome|Chromium|Edg\//.test(ua)) return "desktop"
  if (/Macintosh/.test(ua) && /Safari/.test(ua)) return "safari"
  return "other"
}

export const installManager = typeof window === "undefined" ? null : createInstallManager(window)
